import React, { useRef } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

import { getClientCapabilities } from '@/lib/utils/browserUtils';
import WaitingBlock from '@/lib/components/WaitingBlock';
import ErrorBlock from '@/lib/components/ErrorBlock/ErrorBlock';
import { useRevolut } from './revolutHooks';

import styles from './RevolutPay.module.scss';

/**
 * This component is one of the multiple available payment method in this project.
 * It represents the RevolutPay PSP integration that can deal with multiple bank card types.
 */
const RevolutPay = ({
    config = {},
    loader,
    lyriaUrl,
    onPaymentResult,
    paymentRequestId,
    token,
    successCallbackUrl,
    failureCallbackUrl,
    lineItemName,
}) => {
    const upsellRoot = useRef();
    const buttonRoot = useRef();

    // The api call to Lyria (backend) to trigger RevolutPay payment process.
    const process = React.useCallback(
        async () => {
            return axios({
                method: 'post',
                url: lyriaUrl + '/revolut/process',
                headers: {
                    'Content-Type': 'application/json',
                },
                data: {
                    client_capabilities: getClientCapabilities(),
                    request_token: token,
                    payment_request_id: paymentRequestId,
                    psp: config.id,
                    data: {},
                },
                validateStatus: (status) => status === 200 || status === 400,
            });
        },
        [lyriaUrl, token, paymentRequestId, config.id],
    );

    const checkFinalStatus = React.useCallback(
        async () => {
            return axios({
                method: 'get',
                url: config.final_url,
                headers: {
                    'Content-Type': 'application/json',
                    // this is important to force lyria to reply in json
                    'Accept': 'application/json',
                },
            });
        },
        [config.final_url],
    );

    const { isProcessing, setupError, isReady } = useRevolut(
        config,
        config.publicToken,
        onPaymentResult,
        buttonRoot.current,
        upsellRoot.current,
        process,
        checkFinalStatus,
        successCallbackUrl,
        failureCallbackUrl,
        lineItemName,
    );

    const displayLoader = (!isReady || isProcessing) && !setupError;
    const hideButton = displayLoader || setupError;

    return (
        <>
            {setupError && <ErrorBlock errorCode={'internal'} />}
            {displayLoader && <WaitingBlock loader={loader}>{false}</WaitingBlock>}
            <div className={styles.revolutGroup} style={hideButton ? { display: 'none' } : {}}>
                <div ref={upsellRoot}></div>
                <div
                    id="revolut"
                    ref={buttonRoot}
                    className={styles.revolut}
                    style={hideButton ? { display: 'none' } : {}}
                ></div>
            </div>

        </>
    );
};

RevolutPay.propTypes = {
    /**
     * The specific configuration related to this payment method.
     */
    config: PropTypes.object,
    /**
     * The token to use in order to be able to call the backend when we submit the form data.
     */
    token: PropTypes.string,
    /**
     * The paymentRequestID to use in order to be able to call the backend when we submit the data in the paymentPage.
     */
    paymentRequestId: PropTypes.string,
    /**
     * The backend url to use to pass the form data.
     */
    lyriaUrl: PropTypes.string.isRequired,
    /**
     * Whether the submit button must be displayed or not.
     */
    hidePayButton: PropTypes.bool,
    /**
     * The label of the 'pay' button to display. hidePayButton props must be false of course.
     */
    labelPayButton: PropTypes.string,
    /**
     * The label of the 'pay' button to display for GogglePay and ApplePay.
     */
    labelPayButtonApplePayAndGooglePay: PropTypes.oneOf([
        'buy',
        'book',
        'donate',
        'order',
        'pay',
        'logoOnly',
    ]),
    /**
     * The callback function to trigger when the payment process is starting (to manage RevolutPay exchanges).
     */
    onPaymentStarted: PropTypes.func.isRequired,
    /**
     * The callback function to trigger when the backend return the result of the form data submit.
     */
    onPaymentResult: PropTypes.func.isRequired,
    /**
     * The loader component to use when the payment or drop-in is loading.
     */
    loader: PropTypes.node,
    /**
     * Whether we want to check by default the checkbox to save the payment method.
     */
    savePaymentMethod: PropTypes.bool,
    /**
     * The list of saved payment methods available.
     * The goal is to be able to select one of them and avoid typing it again).
     */
    savedPaymentMethods: PropTypes.array,
    /**
     * A warning message to display for payment at the card input step (mainly to advise the user to save its payment method).
     */
    paymentCardInputWarningMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /**
     * The operation type to process.
     */
    operation: PropTypes.oneOf(['credit', 'payment', 'refund']),
    /**
     * The language of the user in order to display the appropriate translated drop-in (for Apple pay).
     */
    lang: PropTypes.string,
    /**
     * The theme to use (dark or light).
     */
    themeMode: PropTypes.oneOf(['light', 'dark']),
    /**
     * Whether the payment method is disabled, and must not trigger payment or not.
     * Mainly used for Google Pay and Apple Pay buttons.
     */
    isDisabled: PropTypes.bool,
    /**
     * Last resort success redirect url
     */
    successCallbackUrl: PropTypes.string,
    /**
     * Last resort failure redirect url
     */
    failureCallbackUrl: PropTypes.string,
    /**
     * The line item name to display in the RevolutPay drop-in.
     */
    lineItemName: PropTypes.string,
};

RevolutPay.displayName = 'RevolutPay';

export default RevolutPay;
